import React, { useState, useRef, useEffect } from 'react';
import { User, Lock, Home, DoorOpen, QrCode, X, Camera, ArrowRight, Shield } from 'lucide-react';
import jsQR from "jsqr";

export default function LaundryLogin() {
  const [loginMode, setLoginMode] = useState('form');
  const [formData, setFormData] = useState({
    usn: '',
    password: '',
    name: '',
    hostel: 'boys',
    roomNumber: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.usn.trim()) newErrors.usn = 'USN is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.roomNumber.trim()) newErrors.roomNumber = 'Room number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (validateForm()) {
      setIsLoading(true);
      try {
        const response = await fetch("https://hostel-laundry-api.onrender.com/auth/login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            usn: formData.usn,
            password: formData.password
          })
        });

        const data = await response.json();

        if (!response.ok) {
          alert(data.message || "Login failed");
          setIsLoading(false);
          return;
        }

        localStorage.setItem("student", JSON.stringify(data.student));
        window.location.href = "/student-dashboard";

      } catch (err) {
        console.error(err);
        alert("Unable to reach the server. Please try again.");
        setIsLoading(false);
      }
    }
  };

  // DEMO LOGIN — bypasses API, stores dummy student data
  const handleDemoLogin = () => {
    const demoStudent = {
      id: 999,
      usn: "demo2025",
      name: "Alex Johnson",
      password: "demo",
      hostel: "boys",
      room: "B-312",
      roomNumber: "B-312",
      phone: "+91 98765 43210",
      email: "alex.johnson@sjec.ac.in",
      qr_code: null,
      isDemo: true
    };
    localStorage.setItem("student", JSON.stringify(demoStudent));
    localStorage.setItem("demoMode", "true");
    window.location.href = "/student-dashboard";
  };

  // DEMO ADMIN — sets demoAdmin flag and redirects to admin panel
  const handleDemoAdmin = () => {
    localStorage.setItem("demoAdmin", "true");
    window.location.href = "/admin";
  };

  const startQRScanner = async () => {
    setLoginMode("qr");
    setIsScanning(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });

      streamRef.current = stream;

      if (!videoRef.current) {
        return setTimeout(startQRScanner, 200);
      }

      videoRef.current.srcObject = stream;

      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play();
        requestAnimationFrame(scanFrame);
      };

    } catch (err) {
      alert("Camera access denied. Please allow camera permissions.");
    }
  };

  const stopQRScanner = () => {
    setIsScanning(false);
    setLoginMode("form");
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const scanFrame = () => {
    if (!isScanning) return;

    if (!videoRef.current || !canvasRef.current) {
      return requestAnimationFrame(scanFrame);
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const w = video.videoWidth;
    const h = video.videoHeight;

    if (!w || !h) {
      return requestAnimationFrame(scanFrame);
    }

    canvas.width = w;
    canvas.height = h;

    ctx.drawImage(video, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);

    const code = jsQR(imageData.data, w, h, { inversionAttempts: "attemptBoth" });

    if (code) {
      stopQRScanner();
      window.location.href = code.data;
      return;
    }

    requestAnimationFrame(scanFrame);
  };

  const inputBaseClass = "w-full pl-11 pr-4 py-3 border rounded-lg bg-neutral-50 text-sm text-neutral-900 placeholder:text-neutral-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:bg-white";
  const inputNormal = `${inputBaseClass} border-neutral-200 focus:ring-neutral-900 focus:border-transparent`;
  const inputError = `${inputBaseClass} border-red-400 focus:ring-red-500 focus:border-transparent`;

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4 relative overflow-hidden">

      {/* Subtle dot grid background */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}
      />

      <div className="relative w-full max-w-md z-10">
        {loginMode === 'form' ? (
          <>
          <div className="bg-white rounded-xl border border-neutral-200/80 shadow-lg shadow-neutral-200/50 overflow-hidden">

            {/* HEADER */}
            <div className="px-8 pt-10 pb-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-neutral-900 mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-neutral-900 tracking-tight">Laundroid</h1>
              <p className="text-neutral-500 text-sm mt-1 font-normal">Hostel Laundry Management System</p>
            </div>

            <div className="px-8 pb-8">

              {/* Divider */}
              <div className="border-t border-neutral-100 mb-6" />

              <div className="space-y-4">

                {/* USN */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-1.5">
                    University Seat Number
                  </label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                    <input
                      type="text"
                      name="usn"
                      value={formData.usn}
                      onChange={handleInputChange}
                      placeholder="e.g., 1MS21CS045"
                      className={errors.usn ? inputError : inputNormal}
                    />
                  </div>
                  {errors.usn && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.usn}</p>}
                </div>

                {/* NAME */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-1.5">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className={errors.name ? inputError : inputNormal}
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.name}</p>}
                </div>

                {/* PASSWORD */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      className={errors.password ? inputError : inputNormal}
                    />
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.password}</p>}
                </div>

                {/* HOSTEL */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-1.5">
                    Hostel
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, hostel: 'boys' }))}
                      className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg border font-medium text-sm transition-all duration-200 ${formData.hostel === 'boys'
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                        }`}
                    >
                      <Home className="w-4 h-4" /> Boys Hostel
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, hostel: 'girls' }))}
                      className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg border font-medium text-sm transition-all duration-200 ${formData.hostel === 'girls'
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                        }`}
                    >
                      <Home className="w-4 h-4" /> Girls Hostel
                    </button>
                  </div>
                </div>

                {/* ROOM NUMBER */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-1.5">
                    Room Number
                  </label>
                  <div className="relative">
                    <DoorOpen className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                    <input
                      type="text"
                      name="roomNumber"
                      value={formData.roomNumber}
                      onChange={handleInputChange}
                      placeholder="e.g., A-204"
                      className={errors.roomNumber ? inputError : inputNormal}
                    />
                  </div>
                  {errors.roomNumber && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.roomNumber}</p>}
                </div>

                {/* LOGIN BUTTON */}
                <button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 bg-neutral-900 text-white py-3 rounded-lg font-semibold text-sm hover:bg-neutral-800 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                >
                  {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-white text-xs font-medium text-neutral-400 uppercase tracking-wider">
                      or
                    </span>
                  </div>
                </div>

                {/* QR LOGIN BUTTON */}
                <button
                  onClick={startQRScanner}
                  className="w-full flex items-center justify-center gap-2.5 bg-white border border-neutral-200 text-neutral-700 py-3 rounded-lg font-medium text-sm hover:bg-neutral-50 hover:border-neutral-300 transition-all duration-200"
                >
                  <QrCode className="w-4 h-4" />
                  Scan QR Code
                </button>

              </div>

              {/* DEMO SECTION */}
              <div className="mt-6 pt-6 border-t border-neutral-100">
                <p className="text-center text-[11px] font-semibold text-neutral-400 uppercase tracking-widest mb-3">
                  Demo Access
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleDemoLogin}
                    className="group bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-left hover:border-neutral-300 hover:bg-neutral-100/50 transition-all duration-200"
                  >
                    <div className="w-8 h-8 rounded-lg bg-neutral-900 flex items-center justify-center mb-2.5">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <p className="font-semibold text-sm text-neutral-900">Student</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">
                      Dashboard, orders, tracking
                    </p>
                  </button>

                  <button
                    onClick={handleDemoAdmin}
                    className="group bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-left hover:border-neutral-300 hover:bg-neutral-100/50 transition-all duration-200"
                  >
                    <div className="w-8 h-8 rounded-lg bg-neutral-900 flex items-center justify-center mb-2.5">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                    <p className="font-semibold text-sm text-neutral-900">Admin</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">
                      Management console
                    </p>
                  </button>
                </div>

                <p className="text-[11px] text-neutral-400 text-center mt-3">
                  No credentials required for demo access
                </p>
              </div>

              <div className="mt-5 text-center">
                <button
                  onClick={() => window.location.href = "/admin-login"}
                  className="text-neutral-500 hover:text-neutral-900 text-xs font-medium transition-colors duration-200"
                >
                  Administrator Login
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-[11px] text-neutral-400 mt-6">
            Laundroid v1.0 &middot; Hostel Laundry Management
          </p>
          </>
        ) : (

          <div className="bg-white rounded-xl border border-neutral-200/80 shadow-lg shadow-neutral-200/50 overflow-hidden">

            {/* QR HEADER */}
            <div className="bg-neutral-900 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-white" />
                <div>
                  <h2 className="text-base font-semibold text-white">QR Code Scanner</h2>
                  <p className="text-xs text-neutral-400">Position your ID within the frame</p>
                </div>
              </div>
              <button
                onClick={stopQRScanner}
                className="text-white hover:bg-white/10 p-2 rounded-lg transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="relative bg-neutral-950 rounded-xl overflow-hidden aspect-square mb-5">
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain bg-black"
                  playsInline
                />

                <canvas ref={canvasRef} style={{ display: "none" }} />

                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-64 h-64">
                    <div className="absolute inset-0 border-2 border-white/20 rounded-xl"></div>
                    <div className="absolute top-0 left-0 w-10 h-10 border-t-3 border-l-3 border-white rounded-tl-xl"></div>
                    <div className="absolute top-0 right-0 w-10 h-10 border-t-3 border-r-3 border-white rounded-tr-xl"></div>
                    <div className="absolute bottom-0 left-0 w-10 h-10 border-b-3 border-l-3 border-white rounded-bl-xl"></div>
                    <div className="absolute bottom-0 right-0 w-10 h-10 border-b-3 border-r-3 border-white rounded-br-xl"></div>
                    <div className="absolute inset-x-0 top-1/2 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent animate-pulse"></div>
                  </div>
                </div>

                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-lg inline-block">
                    <p className="text-xs font-medium">Align QR code within the frame</p>
                  </div>
                </div>
              </div>

              <button
                onClick={stopQRScanner}
                className="w-full bg-neutral-100 text-neutral-700 py-3 rounded-lg font-medium text-sm hover:bg-neutral-200 transition-all duration-200"
              >
                Cancel
              </button>

              <p className="text-xs text-neutral-400 text-center mt-3">
                Ensure the QR code is well-lit and clearly visible
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
